import { Dialog, DialogContent, DialogProps, Slide } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { capitalDesignTokens } from '@opengov/capital-mui-theme';
import React, { useEffect } from 'react';

import { ISearchContext, SearchContext } from './SearchContext';

const TABLET_LAYOUT_BREAKPOINT = capitalDesignTokens.foundations.layout.breakpoints.mobile.tablet;

const styles = {
  dialog: {
    '& .MuiDialog-container': {
      alignItems: 'flex-start',
      '& > .MuiPaper-root': {
        marginTop: 0
      }
    },
    '& .MuiBackdrop-root': {
      backgroundColor: 'rgba(255,255,255,0.6)'
    }
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: 2,
    paddingBottom: 0
  }
};

export type DialogContainerProps = {
  maxWidth: DialogProps['maxWidth'];
  open?: boolean;
  children?: React.ReactNode | React.ReactNode[];
  onClose?(): void;
};

const SlideDownTransition = React.forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) => <Slide direction="down" ref={ref} {...props} />
);

export const DialogContainer = ({ open, children, onClose, maxWidth }: DialogContainerProps) => {
  const { setLayoutMode } = React.useContext(SearchContext) as ISearchContext<any, any>;
  const dialogRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const updateWidth = () => {
      if (dialogRef.current) {
        const dialogWidth = dialogRef.current.offsetWidth;
        setLayoutMode(dialogWidth < TABLET_LAYOUT_BREAKPOINT ? 'mobile' : 'desktop');
      }
    };

    // Create a ResizeObserver to listen to dialog size changes
    const observer = new ResizeObserver(updateWidth);

    // Initial check
    updateWidth();

    // Observe the dialogRef element if it exists
    if (dialogRef.current) {
      observer.observe(dialogRef.current);
    }
    observer.observe(document.body);

    // Cleanup function to disconnect the observer
    return () => observer.disconnect();
  }, []);

  return (
    <Dialog
      ref={dialogRef}
      sx={styles.dialog}
      fullWidth={true}
      maxWidth={maxWidth}
      open={Boolean(open)}
      onClose={onClose}
      TransitionComponent={SlideDownTransition}
      keepMounted={true}
      aria-labelledby="search-input"
      aria-label="Search Dialog"
    >
      <DialogContent sx={styles.dialogContent}>{children}</DialogContent>
    </Dialog>
  );
};
